import {
  MediaDevicesState,
  MediaDeviceInfoLite,
} from "store/media-devices/types";
import {
  LocalTracksState,
  SetTrackPayload,
  LocalVideoTrackInfo,
  LocalAudioTrackInfo,
} from "store/local-tracks/types";
import { useEffect, useCallback } from "react";
import {
  setLocalVideoTrackInfo,
  setLocalAudioTrackInfo,
  setAudioOutput,
} from "store/local-tracks/slice";
import { useDispatch } from "react-redux";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";

export function useDetectMediaDeviceChange(
  mediaDevices: MediaDevicesState,
  localTracks: LocalTracksState
) {
  const dispatch = useDispatch();

  const updateSelectedDevice = useCallback(
    function updateSelectedDevice(
      setFn: ActionCreatorWithPayload<SetTrackPayload, string>,
      inputs: MediaDeviceInfoLite[],
      currentInput?:
        | LocalVideoTrackInfo
        | LocalAudioTrackInfo
        | LocalAudioTrackInfo
    ) {
      if (currentInput) {
        const deviceIds = inputs.map((v) => v.deviceId);
        const defaultDevice = inputs.find((v) => v.isDefault);

        if (deviceIds.indexOf(currentInput.deviceId) === -1) {
          // The selected input is unavailable (probably got disconnected)
          if (defaultDevice) {
            dispatch(
              setFn({
                deviceId: defaultDevice.deviceId,
                label: defaultDevice.label,
                isDefault: defaultDevice.isDefault,
              })
            );
          } else {
            dispatch(
              setFn({
                deviceId: inputs[0].deviceId,
                label: inputs[0].label,
                isDefault: inputs[0].isDefault,
              })
            );
          }
        } else if (
          currentInput.isDefault &&
          defaultDevice &&
          currentInput.label !== defaultDevice?.label
        ) {
          // default device got plugged back in
          dispatch(
            setFn({
              deviceId: defaultDevice.deviceId,
              label: defaultDevice.label,
              isDefault: defaultDevice.isDefault,
            })
          );
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    updateSelectedDevice(
      setLocalVideoTrackInfo,
      mediaDevices.videoInputs,
      localTracks.videoTrackInfo
    );
  }, [
    updateSelectedDevice,
    mediaDevices.videoInputs,
    localTracks.videoTrackInfo,
  ]);

  useEffect(() => {
    updateSelectedDevice(
      setLocalAudioTrackInfo,
      mediaDevices.audioInputs,
      localTracks.audioTrackInfo
    );
  }, [
    updateSelectedDevice,
    mediaDevices.audioInputs,
    localTracks.audioTrackInfo,
  ]);

  useEffect(() => {
    updateSelectedDevice(
      setAudioOutput,
      mediaDevices.audioOutputs,
      localTracks.audioOutput
    );
  }, [
    updateSelectedDevice,
    mediaDevices.audioOutputs,
    localTracks.audioOutput,
  ]);
}
